/**
 * W1-11a (app-registry-keymap): the command/contribution registry implementation (ARCHITECTURE.md §6.1,
 * DESIGN.md §2.4, §8.1). `registry.ts` wires the app singleton over the discovered modules; this file has no
 * dependency on the module glob (unit-testable with any module list).
 *
 * - Modules are read lazily on first use. Duplicate ids (modules, commands, panels, dialogs, inspector sections,
 *   wizard steps, settings paths, help topics, routes, status items) fail loudly in dev and tests (strict mode)
 *   and are reported (first definition kept) in production.
 * - `attach({ services, when })` binds the services (command contexts, contribution factories, module when-atoms
 *   and `init`). Module atom names are validated then: a clash with a built-in atom (including any name in the
 *   router's `focus.` / `hover.` region namespace) or another module fails like a duplicate id; invalid command
 *   conditions are reported. `whenAtomNames()` = BUILTIN_ATOM_SET (every region atom included) + module atoms.
 * - `run` / `runAny`: unknown or not-attached -> false; the command's `when` must hold and `enabled` must return
 *   true (else false, with the reason passed to `onDisabled`, by default toasted for key, palette and menu runs,
 *   once per burst of wheel ticks or key auto-repeats);
 *   Full-level commands run from the palette at the Guided level ask "Run once" / "Switch to Full" (DESIGN.md
 *   §2.4: commands never disappear); mouse-driven runs of commands that have a shortcut feed the first-use tips
 *   (`ui.noteMouseUse`); a failing command is reported and toasted, never thrown into the caller.
 * - Modal guard (W3-01, UX-005): commands that take over the whole window (`FULL_WINDOW_COMMANDS`: Theater, test
 *   play, play maximised) are disabled while a modal dialog is open (the `dialog` atom), whatever runs them (a
 *   toast action, the palette, a script), so they never start underneath the dialog.
 */

import type { Json, MessageRef, Unsubscribe } from '../../core/util/types';
import type { DialogId } from '../dialogs/types';
import type { CommandArgs } from './commands';
import { m } from './registry.messages';
import { ref } from './text';
import {
  type AnyDialogDef,
  type AppServices,
  type CommandContext,
  type CommandDef,
  type CommandId,
  type CommandSource,
  type DialogDef,
  type FeatureModule,
  type FileDropHandler,
  type HelpTopicDef,
  type InspectorSectionDef,
  type MenuId,
  type PaletteProvider,
  type PanelDef,
  type PanelSlot,
  type Registry,
  type RouteDef,
  type SettingDef,
  type OverlayDef,
  type StatusItemDef,
  type WhenContext,
  type WizardStepDef,
} from './types';
import type { PreviewOverlayProvider, TimelineColumnDef, TimelineDecorationProvider } from '../../render/types';
import { BUILTIN_ATOM_SET, buildWhenContext, compileWhen, isBuiltinAtomName, type CompiledWhen } from './when';
import { detectOs } from './chords';

/** Commands that cover the whole window: disabled while a modal dialog is open (`dialog` atom; UX-005). */
export const FULL_WINDOW_COMMANDS: ReadonlySet<CommandId> = new Set<CommandId>([
  'preview.theater',
  'preview.testPlay',
  'playback.playMaximized',
]);

/**
 * A module authoring problem (duplicate id, when-atom clash, invalid command condition, failed `init`). Developer
 * diagnostics only: no user action can cause one, strict mode (dev builds, tests) throws on the first two, and
 * `message` is English for the console and the startup test, never shown in the UI. Problems a user can cause
 * (their own shortcuts) are `KeymapIssue`s, which carry a translatable `text`.
 */
export interface RegistryIssue {
  readonly kind: 'duplicate' | 'atomClash' | 'invalidWhen' | 'initFailed';
  /** English developer text. */
  readonly message: string;
}

export interface RegistryOptions {
  /** The modules (read lazily on first use). */
  readonly modules: readonly FeatureModule[] | (() => readonly FeatureModule[]);
  /** Throw on duplicate ids and atom clashes (default: dev builds and tests). */
  readonly strict?: boolean;
  /** Where problems are reported (default console.error). */
  readonly report?: (message: string) => void;
  /** Millisecond clock for the disabled-reason toast throttle (default performance.now / Date.now). */
  readonly now?: () => number;
}

/** Trigger event passed to commands (CommandContext.event). */
export type TriggerEvent = KeyboardEvent | PointerEvent | WheelEvent;

export interface RegistryAttachment {
  readonly services: AppServices;
  /** Current editor context (the KeyboardRouter's tracker); default: built from the services alone. */
  readonly when?: () => WhenContext;
  /**
   * A command was not run because its condition (`reason` null) or `enabled` (the reason) said no. Default: the
   * reason of a key, palette or menu run is shown as an info toast (`ui.toast(reason, { kind: 'info' })`), once
   * per burst of wheel ticks or key auto-repeats (see REASON_TOAST_QUIET_MS).
   */
  readonly onDisabled?: (
    cmd: CommandDef,
    reason: MessageRef | null,
    source: CommandSource,
    event?: TriggerEvent,
  ) => void;
  /** A command threw (default: console.error + error toast). */
  readonly onError?: (cmd: CommandDef, error: unknown, source: CommandSource) => void;
}

export interface AppRegistry extends Registry {
  /** Bind services; returns the detach function (disposes module `init` results). */
  attach(a: RegistryAttachment): () => void;
  readonly services: AppServices | null;
  /** Current context for command checks. */
  whenContext(): WhenContext;
  /** Module-registered when-atoms (after attach). */
  moduleAtoms(): Readonly<Record<string, () => boolean>>;
  run<K extends keyof CommandArgs>(
    id: K,
    args: CommandArgs[K],
    source?: CommandSource,
    event?: TriggerEvent,
    at?: number,
  ): Promise<boolean>;
  /** `at`: the beat a context menu was opened at (CommandContext.at); see CR-W3-01-10. */
  runAny(id: CommandId, args?: Json, source?: CommandSource, event?: TriggerEvent, at?: number): Promise<boolean>;
  /** Release a hold command (keyup of a `hold:` binding or of a command with kind 'hold'). */
  release(id: CommandId, source?: CommandSource, event?: TriggerEvent): void;
  /** Whether a command can run now: true, or the reason (null when its condition does not hold or it is unknown). */
  availability(id: CommandId): true | MessageRef | null;
  /** Problems found (duplicates kept in non-strict mode, atom clashes, invalid conditions, failed inits). */
  issues(): readonly RegistryIssue[];
  /** Notified when services are attached or detached (when-atom names may change). */
  onChange(cb: () => void): Unsubscribe;
}

const MOUSE_SOURCES: ReadonlySet<CommandSource> = new Set<CommandSource>(['menu', 'button', 'context']);

/** Sources where a refused command's reason is toasted by default (nothing else on screen explains it). */
const REASON_TOAST_SOURCES: ReadonlySet<CommandSource> = new Set<CommandSource>(['key', 'palette', 'menu']);

/**
 * Quiet period of the disabled-reason toast for continuous triggers (wheel ticks, key auto-repeat): the same command
 * refused for the same reason is toasted again only after this long without such a trigger, so a trackpad pinch or
 * a held arrow key on a locked selection shows the reason once instead of once per event.
 */
export const REASON_TOAST_QUIET_MS = 1500;

/** Wheel ticks and auto-repeat keydowns arrive in bursts (a gesture, a held key); one explanation is enough. */
function isContinuousTrigger(event: TriggerEvent | undefined): boolean {
  if (event === undefined) return false;
  if (event.type === 'wheel') return true;
  return (event.type === 'keydown' || event.type === 'keypress') && (event as KeyboardEvent).repeat;
}

function reasonKeyOf(cmd: CommandDef, reason: MessageRef): string {
  return `${cmd.id}\u0000${reason.key}\u0000${reason.params === undefined ? '' : JSON.stringify(reason.params)}`;
}

function defaultNow(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

interface Index {
  readonly modules: readonly FeatureModule[];
  readonly commands: ReadonlyMap<CommandId, CommandDef>;
  readonly commandList: readonly CommandDef[];
  readonly panels: readonly PanelDef[];
  readonly dialogs: ReadonlyMap<string, AnyDialogDef>;
  readonly sections: readonly InspectorSectionDef[];
  readonly steps: readonly WizardStepDef[];
  readonly settings: readonly SettingDef[];
  readonly help: readonly HelpTopicDef[];
  readonly routes: readonly RouteDef[];
  readonly status: readonly StatusItemDef[];
  readonly overlays: readonly OverlayDef[];
}

const byOrder = <T extends { readonly order: number }>(a: T, b: T): number => a.order - b.order;

function isDev(): boolean {
  try {
    return import.meta.env.DEV;
  } catch {
    return true;
  }
}

export function createRegistry(opts: RegistryOptions): AppRegistry {
  const strict = opts.strict ?? isDev();
  const report =
    opts.report ??
    ((msg: string) => {
      console.error(`registry: ${msg}`);
    });
  const issues: RegistryIssue[] = [];
  const listeners = new Set<() => void>();
  let index: Index | null = null;
  let attachment: RegistryAttachment | null = null;
  let atoms: Readonly<Record<string, () => boolean>> = {};
  let atomNames: ReadonlySet<string> = BUILTIN_ATOM_SET;
  const os = detectOs();
  const whenCache = new Map<string, CompiledWhen | { readonly error: string }>();
  const factoryCache = new WeakMap<AppServices, Map<string, unknown>>();
  const now = opts.now ?? defaultNow;
  /** Last continuous-trigger refusal per command and reason (toast throttle). */
  const lastReasonToast = new Map<string, number>();

  const problem = (kind: RegistryIssue['kind'], message: string): void => {
    issues.push({ kind, message });
    report(message);
  };

  /** Unique-by-id list: duplicates are collected and the first definition kept. */
  function unique<T>(items: readonly T[], idOf: (t: T) => string, what: string, dupes: string[]): T[] {
    const seen = new Map<string, T>();
    for (const item of items) {
      const id = idOf(item);
      if (seen.has(id)) dupes.push(`duplicate ${what} id "${id}"`);
      else seen.set(id, item);
    }
    return [...seen.values()];
  }

  const build = (): Index => {
    const raw = typeof opts.modules === 'function' ? opts.modules() : opts.modules;
    const dupes: string[] = [];
    const modules = unique(raw, (x) => x.id, 'module', dupes);
    const all = <T>(pick: (mod: FeatureModule) => readonly T[] | undefined): T[] =>
      modules.flatMap((x) => pick(x) ?? []);
    const commandList = unique(
      all((x) => x.commands),
      (c) => c.id,
      'command',
      dupes,
    );
    const panels = unique(
      all((x) => x.panels),
      (p) => p.id,
      'panel',
      dupes,
    );
    const dialogList = unique<AnyDialogDef>(
      all((x) => x.dialogs),
      (d) => d.id,
      'dialog',
      dupes,
    );
    const sections = unique(
      all((x) => x.inspectorSections),
      (s) => s.id,
      'inspector section',
      dupes,
    ).sort(byOrder);
    const steps = unique(
      all((x) => x.wizardSteps),
      (s) => s.id,
      'wizard step',
      dupes,
    ).sort(byOrder);
    const settingDefs = unique(
      all((x) => x.settings),
      (s) => s.path,
      'setting',
      dupes,
    );
    const help = unique(
      all((x) => x.helpTopics),
      (h) => h.id,
      'help topic',
      dupes,
    );
    const routes = unique(
      all((x) => x.routes),
      (r) => r.path,
      'route',
      dupes,
    );
    const status = unique(
      all((x) => x.statusItems),
      (s) => s.id,
      'status item',
      dupes,
    );
    const overlays = unique(
      all((x) => x.overlays),
      (o) => o.id,
      'overlay',
      dupes,
    );
    for (const d of dupes) problem('duplicate', d);
    if (strict && dupes.length > 0) throw new Error(`registry: ${dupes.join('; ')}`);
    return {
      modules,
      commands: new Map(commandList.map((c) => [c.id, c])),
      commandList,
      panels,
      dialogs: new Map(dialogList.map((d) => [d.id, d])),
      sections,
      steps,
      settings: settingDefs,
      help,
      routes,
      status,
      overlays,
    };
  };

  const idx = (): Index => (index ??= build());

  /** Derived query results, computed once (modules are static), so they keep their identity across calls. */
  const memo = new Map<string, unknown>();
  const memoize = <T>(key: string, make: () => T): T => {
    if (!memo.has(key)) memo.set(key, make());
    return memo.get(key) as T;
  };

  /** Menu groups: commands by placement order, groups by their first command's order (then name). */
  const buildMenu = (
    id: MenuId,
  ): ReadonlyArray<{ readonly group: string; readonly commands: readonly CommandDef[] }> => {
    const groups = new Map<string, Array<{ readonly order: number; readonly cmd: CommandDef }>>();
    for (const cmd of idx().commandList) {
      for (const p of cmd.menus ?? []) {
        if (p.menu !== id) continue;
        const list = groups.get(p.group) ?? [];
        list.push({ order: p.order, cmd });
        groups.set(p.group, list);
      }
    }
    return [...groups.entries()]
      .map(([group, list]) => ({ group, list: list.sort(byOrder) }))
      .sort((a, b) => (a.list[0]?.order ?? 0) - (b.list[0]?.order ?? 0) || (a.group < b.group ? -1 : 1))
      .map(({ group, list }) => ({ group, commands: list.map((x) => x.cmd) }));
  };

  const notify = (): void => {
    for (const cb of [...listeners]) cb();
  };

  const compiled = (expr: string): CompiledWhen | { readonly error: string } => {
    let c = whenCache.get(expr);
    if (c === undefined) {
      c = compileWhen(expr, atomNames);
      whenCache.set(expr, c);
    }
    return c;
  };

  const whenContext = (): WhenContext => {
    const a = attachment;
    if (a?.when !== undefined) return a.when();
    return buildWhenContext({ services: a?.services ?? null, moduleAtoms: atoms, os });
  };

  /** Cached per services object, so factories run once and contributions keep their identity. */
  const fromFactories = <T>(s: AppServices, key: string, make: () => T): T => {
    let map = factoryCache.get(s);
    if (map === undefined) {
      map = new Map();
      factoryCache.set(s, map);
    }
    if (!map.has(key)) map.set(key, make());
    return map.get(key) as T;
  };

  const callFactory = <T>(mod: FeatureModule, what: string, fn: () => T, fallback: T): T => {
    try {
      return fn();
    } catch (err) {
      report(`module "${mod.id}" ${what} failed: ${err instanceof Error ? err.message : String(err)}`);
      return fallback;
    }
  };

  const makeContext = (
    s: AppServices,
    when: WhenContext,
    source: CommandSource,
    event?: TriggerEvent,
    at?: number,
  ): CommandContext => {
    // Prototype chain keeps the services' live getters (store/engine swap on project open) without reading them.
    const ctx = Object.create(s) as Record<string, unknown>;
    Object.defineProperty(ctx, 'when', { value: when, enumerable: true });
    Object.defineProperty(ctx, 'source', { value: source, enumerable: true });
    if (event !== undefined) Object.defineProperty(ctx, 'event', { value: event, enumerable: true });
    if (at !== undefined && Number.isFinite(at)) Object.defineProperty(ctx, 'at', { value: at, enumerable: true });
    return ctx as unknown as CommandContext;
  };

  /**
   * Condition and `enabled` check: true, the disabled reason, or null (condition false). `args` are the arguments of
   * the run being checked (absent for listings: menus, palette, buttons), so `enabled` can judge a given target.
   */
  const check = (cmd: CommandDef, ctx: CommandContext, args?: Json): true | MessageRef | null => {
    if (cmd.when !== undefined) {
      const c = compiled(cmd.when);
      if ('error' in c || !c.eval(ctx.when)) return null;
    }
    if (FULL_WINDOW_COMMANDS.has(cmd.id) && ctx.when.has('dialog')) return ref(m.closeDialogFirst);
    return cmd.enabled?.(ctx, args) ?? true;
  };

  const reportError = (cmd: CommandDef, err: unknown, source: CommandSource, s: AppServices): void => {
    if (attachment?.onError !== undefined) {
      attachment.onError(cmd, err, source);
      return;
    }
    console.error(`registry: command "${cmd.id}" failed`, err);
    try {
      s.ui.toast(ref(m.commandFailed, { command: s.i18n.t(cmd.title) }), { kind: 'error' });
    } catch {
      // The UI may be unavailable (boot failure); the console has the error.
    }
  };

  /**
   * A command refused to run. Default (no `onDisabled`): a key press, palette pick or menu click whose command gives
   * a reason shows it as an info toast (DESIGN.md §8.2: "disabled with a reason"), since the router has already
   * consumed the key; buttons and context menus render the reason themselves, API callers get `false`. Wheel ticks
   * and auto-repeat keydowns are throttled per command and reason: a burst (each event within
   * REASON_TOAST_QUIET_MS of the previous one) shows the reason once; a discrete press always shows it.
   */
  const disabled = (
    cmd: CommandDef,
    reason: MessageRef | null,
    source: CommandSource,
    s: AppServices,
    event: TriggerEvent | undefined,
  ): void => {
    if (attachment?.onDisabled !== undefined) {
      attachment.onDisabled(cmd, reason, source, event);
      return;
    }
    if (reason === null || !REASON_TOAST_SOURCES.has(source)) return;
    const key = reasonKeyOf(cmd, reason);
    const t = now();
    const last = lastReasonToast.get(key);
    // Every event of the burst (the discrete first press included) extends the quiet period.
    lastReasonToast.set(key, t);
    if (lastReasonToast.size > 256) {
      for (const [k, at] of lastReasonToast) if (t - at >= REASON_TOAST_QUIET_MS) lastReasonToast.delete(k);
    }
    if (isContinuousTrigger(event) && last !== undefined && t - last < REASON_TOAST_QUIET_MS) return;
    try {
      s.ui.toast(reason, { kind: 'info' });
    } catch {
      // The UI may be unavailable (boot failure); the command simply did not run.
    }
  };

  /** Guided level: ask before running a Full-level command from the palette. */
  const confirmFullLevel = async (cmd: CommandDef, s: AppServices): Promise<boolean> => {
    const choice = await s.ui.choose<'once' | 'full'>({
      title: ref(m.fullTitle, { command: s.i18n.t(cmd.title) }),
      body: ref(m.fullBody),
      options: [
        { value: 'once', label: ref(m.fullRunOnce), description: ref(m.fullRunOnceDetail) },
        { value: 'full', label: ref(m.fullSwitch), description: ref(m.fullSwitchDetail) },
      ],
    });
    if (choice === null) return false;
    if (choice === 'full') s.settings.set('general', 'level', 'full');
    return true;
  };

  const runAny = async (
    id: CommandId,
    args?: Json,
    source: CommandSource = 'api',
    event?: TriggerEvent,
    at?: number,
  ): Promise<boolean> => {
    const cmd = idx().commands.get(id);
    if (cmd === undefined) {
      report(`unknown command "${id}"`);
      return false;
    }
    const s = attachment?.services;
    if (s === undefined) {
      report(`command "${id}" run before services were attached`);
      return false;
    }
    const when = whenContext();
    const ctx = makeContext(s, when, source, event, at);
    let ok: true | MessageRef | null;
    try {
      ok = check(cmd, ctx, args);
    } catch (err) {
      reportError(cmd, err, source, s);
      return false;
    }
    if (ok !== true) {
      disabled(cmd, ok, source, s, event);
      return false;
    }
    if (source === 'palette' && cmd.level === 'full' && when.level === 'guided') {
      if (!(await confirmFullLevel(cmd, s))) return false;
    }
    if (MOUSE_SOURCES.has(source)) {
      try {
        if (s.keymap.bindingsFor(id).length > 0) s.ui.noteMouseUse(id);
      } catch {
        // First-use tips are best effort.
      }
    }
    try {
      await cmd.run(ctx, args);
    } catch (err) {
      reportError(cmd, err, source, s);
      return false;
    }
    return true;
  };

  const registry: AppRegistry = {
    get modules() {
      return idx().modules;
    },
    command: (id) => idx().commands.get(id),
    commands: () => idx().commandList,
    panels: (slot: PanelSlot) =>
      memoize(`panels:${slot}`, () =>
        idx()
          .panels.filter((p) => p.slot === slot)
          .sort(byOrder),
      ),
    dialog: <K extends DialogId>(id: K) => idx().dialogs.get(id) as DialogDef<K> | undefined,
    inspectorSections: () => idx().sections,
    wizardSteps: () => idx().steps,
    settings: () => idx().settings,
    timelineColumns: (s) =>
      fromFactories(s, 'columns', () =>
        idx()
          .modules.flatMap((x) =>
            x.timelineColumns === undefined
              ? []
              : callFactory<readonly TimelineColumnDef[]>(x, 'timelineColumns', () => x.timelineColumns?.(s) ?? [], []),
          )
          .sort(byOrder),
      ),
    timelineDecorations: (s) =>
      fromFactories(s, 'decorations', () =>
        idx().modules.flatMap((x) =>
          x.timelineDecorations === undefined
            ? []
            : callFactory<TimelineDecorationProvider[]>(
                x,
                'timelineDecorations',
                () => (x.timelineDecorations === undefined ? [] : [x.timelineDecorations(s)]),
                [],
              ),
        ),
      ),
    previewOverlays: (s) =>
      fromFactories(s, 'overlays', () =>
        idx()
          .modules.flatMap((x) =>
            callFactory<readonly PreviewOverlayProvider[]>(
              x,
              'previewOverlays',
              () => x.previewOverlays?.(s) ?? [],
              [],
            ),
          )
          .sort(byOrder),
      ),
    fileDropHandlers: (s) =>
      fromFactories(s, 'drop', () =>
        idx()
          .modules.flatMap((x) =>
            callFactory<readonly FileDropHandler[]>(x, 'fileDropHandlers', () => x.fileDropHandlers?.(s) ?? [], []),
          )
          .sort((a, b) => b.priority - a.priority),
      ),
    paletteProviders: (s) =>
      fromFactories(s, 'palette', () =>
        idx().modules.flatMap((x) =>
          callFactory<readonly PaletteProvider[]>(x, 'paletteProviders', () => x.paletteProviders?.(s) ?? [], []),
        ),
      ),
    whenAtomNames: () => atomNames,
    helpTopics: () => idx().help,
    routes: () => idx().routes,
    statusItems: (slot) =>
      memoize(`status:${slot}`, () =>
        idx()
          .status.filter((i) => i.slot === slot)
          .sort(byOrder),
      ),
    overlays: () => idx().overlays,
    menu: (id: MenuId) => memoize(`menu:${id}`, () => buildMenu(id)),
    run: (id, args, source, event, at) => runAny(id, args as Json | undefined, source, event, at),
    runAny,
    release(id, source = 'key', event) {
      const cmd = idx().commands.get(id);
      const s = attachment?.services;
      if (cmd?.release === undefined || s === undefined) return;
      try {
        cmd.release(makeContext(s, whenContext(), source, event));
      } catch (err) {
        reportError(cmd, err, source, s);
      }
    },
    availability(id) {
      const cmd = idx().commands.get(id);
      const s = attachment?.services;
      if (cmd === undefined || s === undefined) return null;
      try {
        return check(cmd, makeContext(s, whenContext(), 'api'));
      } catch {
        return null;
      }
    },
    attach(a) {
      if (attachment !== null) throw new Error('registry: already attached (detach first)');
      const { modules } = idx();
      attachment = a;
      // Module when-atoms: one factory call per module, names validated against built-ins and each other.
      const collected: Record<string, () => boolean> = {};
      const clashes: string[] = [];
      for (const mod of modules) {
        if (mod.whenAtoms === undefined) continue;
        const fns = callFactory(mod, 'whenAtoms', () => mod.whenAtoms?.(a.services) ?? {}, {});
        for (const [name, fn] of Object.entries(fns)) {
          if (BUILTIN_ATOM_SET.has(name)) clashes.push(`when-atom "${name}" of module "${mod.id}" is built in`);
          else if (isBuiltinAtomName(name))
            clashes.push(`when-atom "${name}" of module "${mod.id}" uses the built-in focus./hover. region prefix`);
          else if (Object.hasOwn(collected, name))
            clashes.push(`duplicate when-atom id "${name}" (module "${mod.id}")`);
          else collected[name] = fn;
        }
      }
      for (const c of clashes) problem('atomClash', c);
      if (strict && clashes.length > 0) {
        attachment = null;
        throw new Error(`registry: ${clashes.join('; ')}`);
      }
      atoms = collected;
      atomNames = new Set<string>([...BUILTIN_ATOM_SET, ...Object.keys(collected)]);
      whenCache.clear();
      for (const cmd of idx().commandList) {
        if (cmd.when === undefined) continue;
        const c = compiled(cmd.when);
        if ('error' in c) problem('invalidWhen', `command "${cmd.id}": invalid when "${cmd.when}": ${c.error}`);
      }
      // Module init (in module order); disposers run in reverse on detach.
      const disposers: Array<() => void> = [];
      for (const mod of modules) {
        if (mod.init === undefined) continue;
        try {
          const d = mod.init(a.services);
          if (d !== undefined) disposers.push(d);
        } catch (err) {
          problem('initFailed', `module "${mod.id}" init failed: ${err instanceof Error ? err.message : String(err)}`);
        }
      }
      notify();
      let detached = false;
      return () => {
        if (detached) return;
        detached = true;
        for (const d of disposers.reverse()) {
          try {
            d();
          } catch (err) {
            report(`module dispose failed: ${err instanceof Error ? err.message : String(err)}`);
          }
        }
        attachment = null;
        atoms = {};
        atomNames = BUILTIN_ATOM_SET;
        whenCache.clear();
        lastReasonToast.clear();
        notify();
      };
    },
    get services() {
      return attachment?.services ?? null;
    },
    whenContext,
    moduleAtoms: () => atoms,
    issues: () => {
      idx();
      return issues;
    },
    onChange(cb) {
      listeners.add(cb);
      return () => {
        listeners.delete(cb);
      };
    },
  };
  return registry;
}
